import React from 'react';
import { Tooltip as WmsTooltip } from 'react-tooltip';

export const escapeHtml = (value = '') => value.toString()
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

export const getOnboardingStatusClass = (status = '') => {
    switch (status.toString().trim().toLowerCase()) {
        case 'draft':
            return 'activeStatus userRequestStatusDraft';
        case 'submitted':
            return 'activeStatus userRequestStatusSubmitted';
        case 'under review':
            return 'activeStatus userRequestStatusUnderReview';
        case 'approved':
            return 'activeStatus userRequestStatusApproved';
        case 'active':
            return 'activeStatus userRequestStatusActive';
        case 'email verification pending':
            return 'activeStatus userRequestStatusEmailPending';
        case 'email verified':
            return 'activeStatus userRequestStatusEmailVerified';
        case 'rejected':
            return 'activeStatus userRequestStatusRejected';
        default:
            return '';
    }
};

// Shared tooltip anchor id kept for tests/backwards compat, though React tooltips
// now render inline per row and no longer rely on the shared anchor.
export const REJECTION_REASON_TOOLTIP_ID = 'rejection-reason-tooltip';

// Treats null / undefined / whitespace / stringified 'null' as empty so the
// info icon does not leak when the backend serializes null-ish values.
const normalizeRejectionReason = (reason) => {
    if (reason === null || reason === undefined) return '';
    const text = String(reason).trim();
    if (!text) return '';
    const lowered = text.toLowerCase();
    if (lowered === 'null' || lowered === 'undefined') return '';
    return text;
};

// String-returning renderer preserved for existing consumers/tests (produces
// an HTML string suitable for dangerouslySetInnerHTML). Does not include the
// tooltip anchor since react-tooltip needs a real React tree to bind to.
export const renderOnboardingStatus = (status = '') => {
    const displayStatus = status?.toString().trim() || '';
    const statusClass = getOnboardingStatusClass(displayStatus);
    if (!displayStatus) return '';
    if (!statusClass) return escapeHtml(displayStatus);
    return `<span class="statusTd"><span class="${statusClass}">${escapeHtml(displayStatus)}</span></span>`;
};

// React element renderer used by the User Requests table so the WmsTooltip can
// be wired inline (same pattern Inbound uses). Falls back to a plain pill when
// no reason is available.
export const renderOnboardingStatusElement = (status = '', rejectionReason = '', rowKey = '') => {
    const displayStatus = status?.toString().trim() || '';
    const statusClass = getOnboardingStatusClass(displayStatus);
    if (!displayStatus) return null;
    if (!statusClass) return <span className="statusTd">{displayStatus}</span>;

    const isRejected = displayStatus.toLowerCase() === 'rejected';
    const reasonText = normalizeRejectionReason(rejectionReason);
    const tooltipId = `rejection-reason-${rowKey || displayStatus}`;

    return (
        <span className="statusTd">
            <span className={statusClass}>
                {displayStatus}
                {isRejected && reasonText && (
                    <>
                        <i
                            data-tooltip-id={tooltipId}
                            className="fa fa-info-circle ml-2 rejectionReasonIcon"
                            aria-hidden="true"
                        ></i>
                        <WmsTooltip
                            id={tooltipId}
                            content={reasonText}
                            place="top"
                            positionStrategy="fixed"
                            className="rejectionReasonTooltip"
                        />
                    </>
                )}
            </span>
        </span>
    );
};

const RELATIONSHIP_OWNER = 'Owner';
const RELATIONSHIP_REPRESENTATIVE = 'Representative';

// "Signed up as" cell: Owner / Representative plus the signup designation. Signups made
// before ownership capture have no relationship and show "Not captured".
export const renderSignedUpAs = (item = {}) => {
    const relationship = item.RelationshipType;
    if (relationship !== RELATIONSHIP_OWNER && relationship !== RELATIONSHIP_REPRESENTATIVE) {
        return <span className="text-muted">Not captured</span>;
    }
    return (
        <span>
            {relationship}
            {item.Designation && <span className="text-muted d-block">{item.Designation}</span>}
        </span>
    );
};

// "Owner contact" cell. Owner signups point at the account holder; representatives show the
// owner's details; legacy rows show a missing marker with a Request details action.
export const renderOwnerContact = (item, onRequestDetails) => {
    if (item?.RelationshipType === RELATIONSHIP_OWNER) {
        return <span>Same as account holder</span>;
    }
    if (item.RelationshipType === RELATIONSHIP_REPRESENTATIVE) {
        return (
            <span>
                <span className="d-block">{item.OwnerFullName}{item.OwnerRole ? ` (${item.OwnerRole})` : ''}</span>
                {item.OwnerDirectPhone && <span className="d-block">{item.OwnerDirectPhone}</span>}
                {item.OwnerBusinessEmail && <span className="d-block">{item.OwnerBusinessEmail}</span>}
            </span>
        );
    }
    return (
        <span>
            <span className="text-muted d-block">Owner details missing</span>
            {typeof onRequestDetails === 'function' && (
                <button type="button" className="sortBtn linkBtn" onClick={() => onRequestDetails(item)}>
                    Request details
                </button>
            )}
        </span>
    );
};

// Approved onboarding users are no longer editable via the super-admin edit endpoint.
export const isOnboardingEditable = (status) => {
    if (status === null || status === undefined) return true;
    return status.toString().trim().toLowerCase() !== 'approved';
};

export const mapUserRequestRow = (item, index, onRequestDetails) => ({
    ...item,
    listIndex: index,
    isChecked: false,
    statusHtml: renderOnboardingStatusElement(item.OnboardingStatus, item.RejectionReason, item.Id ?? index),
    signedUpAsHtml: renderSignedUpAs(item),
    ownerContactHtml: renderOwnerContact(item, onRequestDetails),
    isEditable: isOnboardingEditable(item.OnboardingStatus),
});
